import { EventRecordType, VisitDataStatus } from '@ecdlink/graphql';
import { MotherDto, VisitDataDto, VisitDto } from '@ecdlink/core';
import { RootState } from '../types';
import { isVisitInProgress } from '@/helpers/visit-helpers';
import { addDays } from 'date-fns';
import { createSelector } from '@reduxjs/toolkit';
import { CompletedVisitsForVisitId } from '../visit/visit.types';

/* -------------------------------
   Base Selectors (primitives)
-------------------------------- */

const selectMothers = (state: RootState) => state.mothers.mothers ?? [];
const selectMothersWeeklyVisits = (state: RootState) =>
  state.mothers.mothersWeeklyVisits ?? [];

/* -------------------------------
   Simple memoized selectors
-------------------------------- */

export const getMothers = createSelector([selectMothers], (mothers) =>
  mothers.filter((mother) =>
    // Check if 'isActive' exists; if it does, use its value; otherwise, return true to include
    'isActive' in mother ? mother.isActive : true
  )
);

export const getMothersWeeklyVisitsSelector = createSelector(
  [selectMothersWeeklyVisits],
  (mothersWeeklyVisits) => mothersWeeklyVisits
);

export const getAllMotherEventRecordTypes = (
  state: RootState
): EventRecordType[] => state.mothers.eventRecordTypes || [];

export const getMothersToSync = createSelector([selectMothers], (mothers) =>
  mothers.filter((item) => 'synced' in item && item.synced === false)
);

export const getMotherVisitsToSync = createSelector(
  [selectMothers],
  (mothers) => {
    return mothers
      .flatMap((mother) => mother.visits || []) // Extract all visits, safely handling missing visits
      .filter((visit) => 'synced' in visit && visit.synced === false);
  }
);

export const getMotherRecordEventsToSync = createSelector(
  [selectMothers],
  (mothers) => {
    return mothers
      .flatMap((mother) => mother.eventRecord || {})
      .filter((record) => 'synced' in record && record?.synced === false);
  }
);

export const getMotherById = (
  state: RootState,
  id: string
): MotherDto | undefined =>
  state.mothers.mothers?.find(
    (mother) =>
      mother?.id === id || mother?.userId === id || mother?.user?.id === id
  );

export const getMotherCountForMonth = (state: RootState): number =>
  state.mothers.motherCountForMonth || 0;

export const getDashboardMotherVisitsSelector = (
  state: RootState,
  motherId: string = ''
): VisitDto[] => {
  if (motherId !== '') {
    const mother = state.mothers.mothers?.find((item) => item.id === motherId);
    return mother?.visits || []; // Returns the visits for the specific mother, or an empty array
  }

  // If no motherId is provided, return a flat array of all visits from all mothers
  return state.mothers.mothers?.flatMap((mother) => mother?.visits || []) || [];
};

export const getMotherCurrentVisitSelector = (
  state: RootState,
  visitId: string,
  motherId: string
): VisitDto | undefined => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const mother = state.mothers.mothers?.find(
    (mother) => mother?.id === motherId
  );
  if (!mother) return undefined;

  const allVisits =
    mother.visits?.filter(
      (item) =>
        item.clientId === motherId &&
        item.visitType?.name !== 'additional_visits'
    ) || [];

  // Priority 1: if a visit id is available, then return visit for id
  if (visitId && visitId !== '') {
    const visits = mother.visits || [];
    for (const element of visits) {
      if (element.id === visitId) {
        return element;
      }
    }
  } else {
    // Priority 1.2: if there is a visit completed, we take the last completed one
    const allAttended = allVisits?.filter((item) => item.attended) || [];
    const allAttendedSorted = allAttended.sort(
      (a, b) =>
        new Date(b.actualVisitDate).getTime() -
        new Date(a.actualVisitDate).getTime()
    );
    if (allAttendedSorted.length !== 0) {
      return allAttendedSorted[0];
    }

    // Priority 2: if there is a visit in progress, we grab the first one
    const inProgressList =
      allVisits?.filter((item) => isVisitInProgress(item)) || [];
    if (inProgressList.length !== 0) {
      return inProgressList[0];
    }

    // Priority 3: grab the latest uncompleted visit from the list
    const noAttended =
      allVisits?.filter(
        (item) => !item.attended && new Date(item.orderDate) >= today
      ) || [];

    if (noAttended) {
      if (noAttended.length !== 0) {
        return noAttended[0];
      }
    } else {
      // Priority 4: grab the latest completed visit from the list
      const lastAttended = allVisits?.filter((item) => item.attended) || [];
      return lastAttended.length
        ? lastAttended.reduce((prev, curr) =>
            (prev.visitType?.order || 0) > (curr.visitType?.order || 0)
              ? prev
              : curr
          )
        : undefined;
    }
  }
};

export const getMotherLastVisitSelector = (
  state: RootState,
  visitId?: string,
  motherId?: string
): VisitDto | undefined => {
  const mother = state.mothers.mothers?.find(
    (mother) => mother?.id === motherId
  );
  if (!mother) return undefined;

  const visits =
    mother.visits?.filter((item) => item.clientId === motherId) || [];

  if (visitId && visitId !== '') {
    const visits = state.mothers.visits || [];
    for (const element of visits) {
      if (element.id === visitId) {
        return element;
      }
    }
  } else {
    const lastAttended = visits?.filter((item) => item.attended) || [];
    return lastAttended.length
      ? lastAttended.reduce((prev, curr) =>
          (prev.visitType?.order || 0) > (curr.visitType?.order || 0)
            ? prev
            : curr
        )
      : undefined;
  }
};

export const getReferralsForMotherSelector = createSelector(
  (state: RootState) => state.mothers.referralsForMother,
  (referrals) => referrals ?? []
);

export const getCompletedReferralsForMotherSelector = (
  state: RootState
): VisitDataStatus[] | undefined =>
  state.mothers.completedReferralsForMother || [];

export const getIsMotherFirstVisitSelector = (
  state: RootState,
  motherId: string
): boolean => {
  const mother = state.mothers.mothers?.find(
    (mother) => mother?.id === motherId
  );
  if (!mother) return false;

  const visits =
    mother.visits?.filter((item) => item.clientId === motherId) || [];

  const attendedVisitsCount = visits?.filter((item) => !!item.attended).length;

  return attendedVisitsCount === 0;
};

export function getMotherNearestPreviousVisitByOrderDate(
  state: RootState,
  currentVisit?: VisitDto,
  motherId?: string
): VisitDto | undefined {
  const mother = state.mothers.mothers?.find(
    (mother) => mother?.id === motherId
  );
  if (!mother) return;

  const visits = mother?.visits;
  if (!visits) return;

  if (currentVisit?.visitType?.name === 'additional_visits') {
    const currentPlannedDate = new Date(currentVisit?.plannedVisitDate!);
    currentPlannedDate?.setHours(23, 59, 59, 0);

    const previousVisits = visits.filter(
      (item) =>
        item.attended &&
        item.actualVisitDate !== null &&
        new Date(item.actualVisitDate) < currentPlannedDate
    );

    if (previousVisits.length === 0) {
      return undefined; // No previous date found
    }

    const nearestDateObject = previousVisits.reduce((previous, current) => {
      if (
        !previous ||
        currentPlannedDate.getTime() -
          new Date(current.actualVisitDate).getTime() <
          currentPlannedDate.getTime() -
            new Date(previous.actualVisitDate).getTime()
      ) {
        return current;
      }
      return previous;
    });

    return nearestDateObject;
  }

  const allAttended = visits?.filter((item) => item.attended) || [];
  const previousVisits = allAttended.sort(
    (a, b) =>
      new Date(b.actualVisitDate).getTime() -
      new Date(a.actualVisitDate).getTime()
  );

  if (previousVisits.length !== 0) {
    return previousVisits[0];
  } else {
    return undefined;
  }
}

// export const getMotherFirstVisitSelector = (
//   state: RootState,
//   motherId: string
// ): VisitDto | null => {
//   const mother = state.mothers.mothers?.find((mother) => mother?.id === motherId);
//   if (!mother) return null;

//   const visits = mother?.visits;
//   if (!visits) return null;

//   const filteredVisits = visits.filter((visit) => {
//     const orderDate = new Date(visit.orderDate);
//     return (
//       orderDate.getFullYear() !== 0 &&
//       visit.attended &&
//       visit.visitType?.normalizedName !== 'Additional visits'
//     );
//   });
//   const firstVisit = filteredVisits?.reduce(
//     (oldest: VisitDto | null, current: VisitDto) => {
//       const currentPlannedVisitDate = new Date(current.orderDate);
//       if (!oldest || currentPlannedVisitDate < new Date(oldest.orderDate)) {
//         return current;
//       }
//       return oldest;
//     },
//     null
//   );

//   return firstVisit;
// };

export const getIsMotherFirstVisitForDateSelector = (
  state: RootState,
  expectedDeliveryDate: string | Date | undefined,
  daysAfter: number,
  daysBefore: number,
  motherId: string
): boolean => {
  const mother = state.mothers.mothers?.find(
    (mother) => mother?.id === motherId
  );
  if (!mother) return false;

  const visits = mother?.visits;
  if (!visits) return false;

  const conceptionDate = addDays(new Date(expectedDeliveryDate!), -280);
  const dateAfter = addDays(new Date(conceptionDate!), daysAfter);
  const dateBefore = addDays(new Date(conceptionDate!), daysBefore);

  const attendedVisits = visits?.filter(
    (item) =>
      item.visitType?.name !== 'additional_visit' &&
      !!item.attended &&
      new Date(item.plannedVisitDate).getTime() >= dateAfter.getTime() &&
      new Date(item.plannedVisitDate).getTime() <= dateBefore.getTime()
  );

  return attendedVisits?.length === 0;
};

//-----------------------------------------------------------------
// new selectors reading data from state in stead of back-end calls
//-----------------------------------------------------------------
export const getVisitAnswersForMotherSelector = (
  state: RootState,
  clientId: string,
  visitId: string = '',
  visitName: string = '',
  visitSection: string = ''
): VisitDataDto[] => {
  const mother = state.mothers.mothers?.find((m) => m.id === clientId);
  if (!mother) return [];

  const visits = mother.visits;
  if (!visits) return [];

  let visit: VisitDto;

  if (visitId !== '') {
    visit = visits?.find((v) => v.id === visitId)!;
  } else {
    const allAttended = visits?.filter((item) => item.attended) || [];
    const previousVisits = allAttended.sort(
      (a, b) =>
        new Date(b.actualVisitDate).getTime() -
        new Date(a.actualVisitDate).getTime()
    );
    visit = previousVisits[0];
  }

  if (visitName === '' && visitSection === '') {
    return (visit?.visitAnswers as VisitDataDto[]) || [];
  }

  if (visitName !== '' && visitSection === '') {
    return visit.visitAnswers?.filter((a) => a.visitName === visitName) ?? [];
  }

  if (visitName !== '' && visitSection !== '') {
    return (
      visit.visitAnswers?.filter(
        (a) => a.visitName === visitName && a.visitSection === visitSection
      ) ?? []
    );
  }

  return [];
};

// export const getCompletedVisitsForVisitIdSelector = (
//   state: RootState,
//   clientId: string,
//   visitId: string
// ): string[] => {
//   const mother = state.mothers.mothers?.find((m) => m.id === clientId);
//   if (!mother) return [];

//   const visit = mother.visits?.find((v) => v.id === visitId);
//   if (!visit) return [];

//   return visit?.visitAnswers?.map((x) => x.visitName!) || [];
// };

// export const getOfflineMotherVisitData = (
//   state: RootState,
//   clientId: string
// ): string[] => {
//   const mother = state.mothers.mothers?.find((m) => m.id === clientId);
//   if (!mother || !mother.visits) return [];

//   const visitIds = new Set<string>();

//   for (const visit of mother.visits) {
//     if (!visit?.visitAnswers) continue;

//     for (const answer of visit.visitAnswers) {
//       if (!answer.synced) {
//         visitIds.add(visit.id);
//         break;
//       }
//     }
//   }

//   return Array.from(visitIds);
// };

export const getMomCompletedVisitsByVisitIdSelector = (
  state: RootState,
  clientId: string,
  visitId: string
): CompletedVisitsForVisitId | undefined => {
  const mother = state.mothers.mothers?.find((m) => m.id === clientId);
  if (!mother?.visits) return undefined;

  const visit = mother.visits.find((v) => v.id === visitId);
  if (!visit?.visitAnswers) return undefined;

  // Use Set to remove duplicate visit names
  const uniqueVisitNames = Array.from(
    new Set(visit.visitAnswers.map((a) => a.visitName!))
  );

  return {
    visitId,
    visits: uniqueVisitNames,
  };
};

export const getMotherByCaregiverIdSelector = (
  state: RootState,
  caregiverId: string
): MotherDto | undefined => {
  return state.mothers.mothers?.find(
    (i) => i.linkedCaregiverId === caregiverId
  );
};
